/* HYP infinite scroll for collection AND search pages — prefetch edition v3.
   - The NEXT page is fetched in the background as soon as the current one
     renders, so appending is instant when the user approaches the bottom.
   - If the user outruns loading (slams to the very bottom), pages chain-load
     back-to-back until the sentinel is out of range.
   - Survives facet filtering re-renders via MutationObserver re-init.
   - Works on any section matching SECTION_SEL that has a <product-list> and a
     nav.pagination a[rel=next] (main-collection and main-search both do).
   v3 (9/16): Shopify throttles rapid storefront fetches with 429. v2 parsed
   the 429 body, found no section/next link and declared "last page", so
   scrolling silently stopped after ~3 pages (visible on search, where POS
   twins are hidden per card so each page adds little height and the chain
   loader fires fast). Now: non-OK responses and missing sections are RETRIED
   with backoff, "done" is only set when a real page has no next link, chained
   loads are spaced out, and a failed pump re-arms itself. */
(function () {
  'use strict';

  var SECTION_SEL = '.shopify-section--main-collection, .shopify-section--main-search';
  var LOOKAHEAD_PX = 2400;   // how far below the viewport counts as "near"
  var CHAIN_GAP_MS = 350;    // pause between back-to-back page loads (collections)
  var SEARCH_GAP_MS = 1200;  // /search is rate-limited much harder than collections
  var MAX_TRIES = 6;         // per page, with exponential backoff / Retry-After
  var appending = false;

  function getSection() {
    return document.querySelector(SECTION_SEL);
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function setup(section) {
    var list = section.querySelector('product-list');
    var nextLink = section.querySelector('nav.pagination a[rel="next"]');
    if (!list || !nextLink) return;
    if (section.querySelector('.hyp-inf-sentinel')) return; // already active

    var pagination = nextLink.closest('nav.pagination');
    if (pagination) pagination.style.display = 'none';

    var sentinel = document.createElement('div');
    sentinel.className = 'hyp-inf-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;width:100%;';
    list.parentNode.insertBefore(sentinel, list.nextSibling);

    var nextUrl = nextLink.href;
    var isSearch = location.pathname.indexOf('/search') === 0;
    var prefetchPromise = null;
    var busy = false;
    var done = false;

    // Fetch one page. Resolves { nodes, next } only for a real, parsed page.
    // Rejects after MAX_TRIES so the caller can re-arm instead of giving up.
    function fetchPage(url, attempt) {
      attempt = attempt || 1;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) {
            var err = new Error('HTTP ' + r.status);
            err.retryable = r.status === 429 || r.status === 430 || r.status >= 500;
            var ra = parseFloat(r.headers.get('Retry-After'));
            if (ra > 0) err.retryAfter = ra * 1000;
            throw err;
          }
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newSection = doc.querySelector(SECTION_SEL);
          if (!newSection) {
            // 200 but not a storefront section (challenge/interstitial) — retry.
            var e = new Error('no section in response');
            e.retryable = true;
            throw e;
          }
          var newList = newSection.querySelector('product-list');
          var newNext = newSection.querySelector('nav.pagination a[rel="next"]');
          var nodes = [];
          if (newList) {
            while (newList.firstChild) {
              nodes.push(newList.firstChild);
              newList.removeChild(newList.firstChild);
            }
          }
          return { nodes: nodes, next: newNext ? newNext.href : null };
        })
        .catch(function (err) {
          if (err && err.retryable && attempt < MAX_TRIES) {
            var delay = err.retryAfter || 600 * Math.pow(2, attempt - 1); // 600, 1200, 2400, 4800
            if (window.console) console.warn('[hyp-scroll] retry', attempt, err.message, 'in', delay + 'ms');
            return wait(delay).then(function () { return fetchPage(url, attempt + 1); });
          }
          throw err;
        });
    }

    function startPrefetch() {
      if (!prefetchPromise && nextUrl && !done) {
        prefetchPromise = fetchPage(nextUrl);
      }
    }

    function appendResult(res) {
      appending = true;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < res.nodes.length; i++) frag.appendChild(res.nodes[i]);
      list.appendChild(frag);
      setTimeout(function () { appending = false; }, 0);

      nextUrl = res.next;
      if (!nextUrl) {
        done = true;
        io.disconnect();
        sentinel.remove();
      }
    }

    function sentinelNear() {
      if (done || !sentinel.isConnected) return false;
      var rect = sentinel.getBoundingClientRect();
      return rect.top < window.innerHeight + LOOKAHEAD_PX;
    }

    function pump() {
      if (busy || done) return;
      if (!prefetchPromise) {
        if (!nextUrl) { done = true; return; }
        startPrefetch();
      }
      busy = true;
      prefetchPromise
        .then(function (res) {
          prefetchPromise = null;
          busy = false;
          appendResult(res);
          if (!done) {
            startPrefetch();            // warm the following page immediately
            if (sentinelNear()) {       // user is outrunning us: chain-load,
              setTimeout(pump, isSearch ? SEARCH_GAP_MS : CHAIN_GAP_MS); // don't hammer the storefront
            }
          }
        })
        .catch(function (err) {
          if (window.console) console.warn('[hyp-scroll] page load failed, will re-arm:', err && err.message);
          // Exhausted retries. Don't mark done — re-arm and try again later
          // if the sentinel is still in range (IO won't refire on its own).
          busy = false;
          prefetchPromise = null;
          setTimeout(function () { if (sentinelNear()) pump(); }, 4000);
        });
    }

    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) pump();
    }, { rootMargin: LOOKAHEAD_PX + 'px 0px' });

    io.observe(sentinel);

    // Warm page 2 right away — by the time anyone scrolls, it's ready.
    startPrefetch();
  }

  function scan() {
    if (appending) return;
    var section = getSection();
    if (section) setup(section);
  }

  function init() {
    var section = getSection();
    if (!section) return;
    setup(section);

    // The facet filters re-render the section content in place; re-attach
    // whenever the grid is replaced.
    var mo = new MutationObserver(function () {
      if (appending) return;
      window.clearTimeout(init._t);
      init._t = window.setTimeout(scan, 150);
    });
    mo.observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
